import { CalendarDaysIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { useCallback, useEffect, useRef, useState } from "react";
import { DayPicker, type Matcher } from "react-day-picker";
import dayPickerStyles from "react-day-picker/style.module.css";
import { useClientTranslations } from "../../../lib/i18n/client";
import styles from "./CycleDatePicker.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

// Parse YYYY-MM-DD as a local-time Date (avoids UTC-midnight timezone drift).
// Tolerates a full ISO datetime (e.g. "2026-06-17T00:00:00.000Z") by reading
// only the date part, so a persisted value re-fetched from the API still
// prefills the picker (86ey31vmb).
function parseDateValue(s: string): Date | undefined {
	if (!s) return undefined;
	const [y, m, d] = s.slice(0, 10).split("-").map(Number);
	if (!y || !m || !d) return undefined;
	return new Date(y, m - 1, d);
}

// Format a Date back to YYYY-MM-DD in local time, matching input[type=date] output.
function formatDateValue(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

interface CycleDatePickerProps {
	/** Accessible label for the trigger button (replaces htmlFor/id wiring). */
	label?: string;
	/** YYYY-MM-DD string, or empty string when unset — same format as input[type=date]. */
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	/** react-day-picker disabled matcher — pass e.g. `{ after: today }` to restrict to past dates. */
	disabledDays?: Matcher | Matcher[];
	/** Latest navigable month (react-day-picker v9 `endMonth`). Pass `new Date()` to stop users from paging into future months (e.g. last-period date). */
	endMonth?: Date;
	/** Open the calendar popover upward instead of downward — use in modals where the picker is near the bottom. */
	openUpward?: boolean;
}

export function CycleDatePicker({
	label,
	value,
	onChange,
	placeholder,
	disabled = false,
	disabledDays,
	endMonth,
	openUpward = false,
}: CycleDatePickerProps) {
	// Default ("common") namespace — this picker only needs the active locale and
	// direction, so it must not force-load the onboarding bundle on non-onboarding
	// surfaces (Get Ready / Journey modals).
	const { locale, isRTL } = useClientTranslations();
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const today = new Date();

	const selected = parseDateValue(value);
	const displayValue = selected
		? format(selected, "d MMM yyyy", { locale: dateFnsLocale })
		: null;

	const close = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return;
		const onPointer = (e: PointerEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
				close();
			}
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open, close]);

	return (
		<div ref={rootRef} className={styles.root}>
			<button
				type="button"
				disabled={disabled}
				className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				aria-haspopup="dialog"
				aria-label={label}
			>
				<CalendarDaysIcon className={styles.icon} aria-hidden="true" />
				<span
					className={`${styles.triggerLabel} ${!displayValue ? styles.triggerLabelEmpty : ""}`}
				>
					{displayValue ?? placeholder}
				</span>
				<ChevronDownIcon
					className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
					aria-hidden="true"
				/>
			</button>

			{open && (
				<div
					className={openUpward ? styles.popoverUp : styles.popover}
					role="dialog"
					aria-label={label}
				>
					<DayPicker
						mode="single"
						selected={selected}
						onSelect={(d) => {
							if (d) onChange(formatDateValue(d));
							setOpen(false);
						}}
						defaultMonth={selected ?? today}
						endMonth={endMonth}
						disabled={disabledDays}
						locale={dateFnsLocale}
						dir={isRTL ? "rtl" : "ltr"}
						weekStartsOn={1}
						classNames={{
							...dayPickerStyles,
							// Combine both classes: dayPickerStyles.root provides all the
							// default CSS variable definitions (--rdp-disabled-opacity etc.);
							// styles.calendar overrides --rdp-accent-color to our purple.
							root: `${dayPickerStyles.root} ${styles.calendar}`,
						}}
					/>
				</div>
			)}
		</div>
	);
}
